import { Core } from "@faire/design-tokens";
import { useMutation as useDeleteHiddenBrandUserFeedback } from "@faire/web-api/api/v2/search/feedback/hide-brand/brandToken/delete-hooks";
import { useQuery as useGetUserFeedbackBrandHiddenQuery } from "@faire/web-api/api/v2/search/feedback/hide-brand/brandToken/get-hooks";
import { Button } from "@faire/web/slate/Button";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

interface UserFeedbackHiddenToastProps {
  brandToken?: string;
}

export const UserFeedbackHiddenToast: React.FC<
  UserFeedbackHiddenToastProps
> = ({ brandToken }) => {
  const { data } = useGetUserFeedbackBrandHiddenQuery(brandToken ?? "");
  const { mutate: undoHiddenBrand } = useDeleteHiddenBrandUserFeedback();
  const { isTabletAndAbove } = useViewport();
  if (!data?.is_brand_hidden) {
    return null;
  }

  return (
    <MessageContainer>
      <Row align="center" gap="1/2x">
        <Typography color={Core.text.inverse}>
          <StrictLocalMsg
            defaultMessage="You've hidden this brand and won't see it in recommendations."
            description={{
              text: "Message to notify retailer that brand is not being shown in recommendations",
              img: "https://cdn.faire.com/fastly/b5540a137acaf698270dd59a8ebd7e02424868b6ca941030e6698becb646c108.png",
            }}
          />
        </Typography>
        <Button
          variant={isTabletAndAbove ? "tertiary" : "primary"}
          tertiaryColor={Core.text.inverse}
          onClick={() => undoHiddenBrand([brandToken ?? ""])}
        >
          <Typography
            color={Core.text.inverse}
            variant={isTabletAndAbove ? undefined : "paragraphSansMedium"}
          >
            <StrictLocalMsg
              defaultMessage="Undo"
              description={{
                text: "Button to undo hiding a brand",
                img: "https://cdn.faire.com/fastly/b5540a137acaf698270dd59a8ebd7e02424868b6ca941030e6698becb646c108.png",
              }}
            />
          </Typography>
        </Button>
      </Row>
    </MessageContainer>
  );
};

const MessageContainer = styled.div`
  border-radius: ${getSpacing("1x")};
  position: fixed;
  ${responsiveMediaQueries.mobile} {
    border-radius: 0;
    left: ${getSpacing("2x")};
    right: ${getSpacing("2x")};
  }
  ${responsiveMediaQueries.tablet} {
    left: ${getSpacing("2x")};
    right: ${getSpacing("2x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    width: 100%;
    position: sticky;
  }
  background-color: ${Core.icon.default};
  min-height: 52px;
  bottom: ${getSpacing("3x")};
  padding: ${getSpacing("2x")};
  z-index: 99;
`;
